import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  onCategoryClick?: () => void;
}

export default function Navbar({ onCategoryClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategoryToggle = () => {
    setMobileCategoryOpen(!mobileCategoryOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCategoryOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          className="flex items-center gap-2 group"
        >
          <span className="text-xl md:text-2xl font-heading text-background-50 group-hover:text-primary-300 transition-colors"
            style={scrolled ? { color: 'oklch(var(--primary-500))' } : {}}
          >
            Kakobuy Finds
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/80 hover:text-background-50'
            }`}
          >
            Home
          </Link>
          <Link
            to="/blog/kakobuy-spreadsheet-ultimate-guide-2026"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/80 hover:text-background-50'
            }`}
          >
            Guides
          </Link>
          <button
            onClick={onCategoryClick}
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/80 hover:text-background-50'
            }`}
          >
            Categories
          </button>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-medium bg-primary-500 text-background-50 px-5 py-2 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${scrolled ? 'text-foreground-900' : 'text-background-50'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors py-2.5 px-3 rounded-lg flex items-center gap-2"
            onClick={closeMobileMenu}
          >
            <i className="ri-home-line text-base w-5 h-5 flex items-center justify-center"></i>
            Home
          </Link>
          <Link
            to="/blog/kakobuy-spreadsheet-ultimate-guide-2026"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors py-2.5 px-3 rounded-lg flex items-center gap-2"
            onClick={closeMobileMenu}
          >
            <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
            Guides
          </Link>

          <div>
            <button
              onClick={handleCategoryToggle}
              className="w-full text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors py-2.5 px-3 rounded-lg flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-base w-5 h-5 flex items-center justify-center"></i>
                Categories
              </span>
              <i className={`text-sm transition-transform duration-200 ${mobileCategoryOpen ? 'ri-arrow-up-s-line rotate-0' : 'ri-arrow-down-s-line'}`}></i>
            </button>

            {mobileCategoryOpen && (
              <div className="ml-3 mt-1 mb-1 border-l-2 border-background-200/70 pl-3 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between text-xs text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors py-2 px-3 rounded-lg group"
                    onClick={closeMobileMenu}
                  >
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-400 text-xs"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/blog/best-kakobuy-clothing-finds"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors py-2.5 px-3 rounded-lg flex items-center gap-2"
            onClick={closeMobileMenu}
          >
            <i className="ri-shirt-line text-base w-5 h-5 flex items-center justify-center"></i>
            Clothing Finds
          </Link>
          <Link
            to="/blog/how-to-read-kakobuy-product-lists"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors py-2.5 px-3 rounded-lg flex items-center gap-2"
            onClick={closeMobileMenu}
          >
            <i className="ri-file-list-3-line text-base w-5 h-5 flex items-center justify-center"></i>
            Pro Guide
          </Link>

          <div className="pt-3 mt-2 border-t border-background-200/70">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block text-sm font-medium bg-primary-500 text-background-50 px-5 py-2.5 rounded-full hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
              onClick={closeMobileMenu}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}